/**
 * The growth stages a garden's plants are filed under (Seeding, Maturity...),
 * and the garden's changes to them: a name, a colour, hidden, an order, and
 * stages of its own.
 *
 * Catalogue stages are shared by everyone; a garden's changes and its own
 * stages belong to the garden (B89, the owner's decision of 2026-10-25): the
 * server keeps them against the garden's owner and applies them as the stages
 * are read, so everyone tending a garden (the X-Grow-Garden header apiClient
 * sends) sees and uses the same ones, and nothing here edits another garden's.
 *
 * Endpoints:
 * - GET    /stages?lang= → Stage[] (a bare list: the catalogue and the garden's
 *   own, with the garden's names, colours, hiding and order applied). Each name is the
 *   server's, said in the app's language: the gardener's own where they
 *   renamed it, else the catalogue stage's word (Last Harvest is
 *   "Harvesting"; services/stages/stageKinds), so the app prints it as sent.
 * - POST   /stages { name, colorKey } → { stage_id }.
 * - PATCH  /stages/:id StageEdit.
 * - PUT    /stages/order { order: number[] }.
 * - DELETE /stages/:id; 403 for a catalogue stage, 409 for an own stage
 *   photos are still filed under.
 *
 * Who uses it: shared/context/StagesContext (every screen's stages),
 * user/UserProfile and the profile's StageSettings, each as getStages(language).
 * Caching: [STAGES_QUERY_KEY, language] ('stages', then the language, so a
 * change of language reads the names again), invalidated by
 * [STAGES_QUERY_KEY] with 'plants' after an edit.
 * Auth: the reading is optional (signed out, the plain catalogue); the
 * changes need the signed-in token.
 *
 * @module stages
 */

import { apiClient } from './apiClient';
import { Stage } from '../types/stage';
import { getLanguage } from '@/shared/i18n/i18nSetup';

/**
 * Every stage the garden being tended has, with its changes applied and each
 * named by the server in a language.
 *
 * @param lang - The language of the names; the app's when not given.
 * @returns The stages, in their order.
 * @throws The request's error.
 */
export async function getStages(lang?: string): Promise<Stage[]> {
  const response = await apiClient.get('/stages', { params: { lang: lang ?? getLanguage() } });
  return response.data;
}


/** What a gardener may change about a stage. */
export interface StageEdit {
  /** Their own name for it; omit to leave it alone. */
  name?: string;
  /** A key from COLOR_KEYS; omit to leave it alone. */
  colorKey?: string;
  /** Whether to keep it out of the pickers; omit to leave it alone. */
  isHidden?: boolean;
}

/**
 * Adds a stage of the garden's own (made while tending someone else's garden, it is that garden's).
 *
 * It is a labelling stage only. The planting timeline calculates from
 * stage_type, which the server deliberately leaves empty on these, so a
 * gardener inventing "Netted" cannot disturb anybody's planting dates.
 *
 * @param name - Its name.
 * @param colorKey - A key from COLOR_KEYS; leave it out and the server uses the Grow setting plants.NEW_STAGE_COLOR.
 * @returns The new stage's id.
 */
export async function createStage(name: string, colorKey?: string): Promise<number> {
  const response = await apiClient.post<{ stage_id: number }>('/stages', { name, colorKey });
  return response.data.stage_id;
}

/**
 * Changes a stage for this garden only.
 *
 * Catalogue stages are shared, so nothing is edited in place: the difference
 * is stored against the garden and applied when stages are read. Renaming
 * Seeding renames it for everyone tending this garden and for nobody else.
 *
 * @param stageId - The stage.
 * @param edit - What to change; anything left out stays.
 */
export async function updateStage(stageId: number, edit: StageEdit): Promise<void> {
  await apiClient.patch(`/stages/${stageId}`, edit);
}

/**
 * Keeps the stages in this order, for this garden only.
 *
 * @param stageIds - Every stage the editor shows, in the order wanted.
 */
export async function reorderStages(stageIds: number[]): Promise<void> {
  await apiClient.put('/stages/order', { order: stageIds });
}

/**
 * Removes a stage the garden added.
 *
 * The server refuses a catalogue stage, and refuses an own stage that photos or stage history
 * are still filed under — hiding is the answer in both cases. Those arrive as
 * 403 and 409 respectively, carrying a code the caller can read.
 *
 * @param stageId - The stage.
 */
export async function deleteStage(stageId: number): Promise<void> {
  await apiClient.delete(`/stages/${stageId}`);
}
